import { ConfigurableProduct, Maybe, useFetchCartHeaderQuery } from '@magentoTypes'
import { useTranslation } from 'next-i18next'
import { CrossSell } from '~/modules/CrossSell/CrossSell'
import { Image } from '@next/image'
import { useCartPreviewProductQuery } from '@magentoTypes'
import { useQueryClient } from '@tanstack/react-query'
import { createMagentoClient } from '~/graphql/magentoClient'
import { Price } from './Price'
import { useCartId } from '~/hooks/useCartId'
import { useFormatPrice } from '~/hooks/useFormatPrice'
import { LoadingCartPreview } from './LoadingCartPreview'
import { useElementSize } from '@charlietango/hooks'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'
import { DEFAULT_LOCALE } from '~/config/constants'
import { useRouter } from 'next/router'
import { parseMagentoGraphqlError, resettableErrors } from '~/lib/parseMagentoGraphlError'
import { ButtonMain } from '~/elements/Button/ButtonMain'
type Props = {
  closeFlyout: () => void
  addedProduct: {
    sku: Maybe<string> | undefined
    sizeUid: string | undefined
  }
}

export const CartPreview = ({ closeFlyout, addedProduct }: Props) => {
  const queryClient = useQueryClient()
  const [cartId, _loading, resetCartId] = useCartId()
  const { formatPrice } = useFormatPrice()
  const { t } = useTranslation()
  const router = useRouter()
  const finalLocale = router.locale ? createLanguageRegionLocale(router.locale) : DEFAULT_LOCALE
  const magentoClient = createMagentoClient({ usePost: false, queryClient, locale: finalLocale })
  const { data, isLoading } = useCartPreviewProductQuery(
    magentoClient,
    { sku: addedProduct?.sku as string },
    { enabled: !!addedProduct?.sku },
  )
  const { data: cartData } = useFetchCartHeaderQuery(
    magentoClient,
    {
      cartId,
    },
    {
      enabled: !!cartId,
      async onError(error) {
        const parsedError = parseMagentoGraphqlError(error)

        if (
          typeof parsedError?.extensions?.category !== 'undefined' &&
          resettableErrors.includes(parsedError?.extensions?.category)
        ) {
          await resetCartId()
        } else {
        }
      },
    },
  )

  const simpleProduct = (data?.products?.items?.[0] as ConfigurableProduct)?.variants?.find(
    (item) => item?.attributes?.[0]?.uid === addedProduct?.sizeUid,
  )

  return (
    <div className="vh100 grid grid-cols-1 grid-rows-[auto_1fr_auto] overflow-y-auto bg-primary-white lg:py-[38px] lg:px-8">
      <p className="mb-16 flex items-center gap-4 p-4 pb-0 text-h4-mobile font-medium lg:p-0">
        <Image
          src={'/images/Icons/checked.svg'}
          width={30}
          height={30}
          placeholder="empty"
          alt="usp-icon"
        />
        <span>{t('Added to cart')}</span>
      </p>
      <div className="flex flex-col pb-2">
        {isLoading ? (
          <LoadingCartPreview className="mb-6" />
        ) : data ? (
          <div className="mb-6 grid grid-cols-[auto_1fr] grid-rows-1 items-center gap-6 px-4 lg:px-0">
            <Image
              src={data?.products?.items?.[0]?.small_image?.url as string}
              alt={data?.products?.items?.[0]?.small_image?.label as string}
              height={150}
              width={112.5}
            />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto] lg:gap-6">
              <div>
                <p className="mb-4">{simpleProduct?.product?.name}</p>
                <p className="text-p-small font-extralight">
                  {t('Size')} {simpleProduct?.attributes?.[0]?.label}
                </p>
              </div>
              <div className="flex max-w-[100px] gap-2 text-p font-extralight lg:flex-col-reverse lg:justify-end lg:gap-1 lg:content-[start]">
                <Price
                  regPriceClass="mr-0"
                  salePriceClass="lgx:mr-0"
                  priceRange={simpleProduct?.product?.price_range}
                />
              </div>
            </div>
          </div>
        ) : null}
        <div className="grid grid-cols-[1fr_auto] border-t border-b border-grey-border px-4 py-6 lg:-mx-8 lg:px-8">
          <div>
            <p>
              <span className="mr-2 font-medium">{t('Price')}</span>
              <span>
                ({cartData?.cart?.total_quantity}{' '}
                {(cartData?.cart?.total_quantity as number) > 1 ? t('Products') : t('Product')})
              </span>
            </p>
          </div>

          {cartData?.cart?.prices?.grand_total ? (
            <p className="font-medium">{formatPrice(cartData?.cart?.prices?.grand_total)}</p>
          ) : null}
        </div>
        <div className="lg:-mr-8">
          <CrossSell
            className="pt-8 lg:pr-8"
            listClassName="pb-[140px]"
            productSize={
              simpleProduct?.product?.attribute_set_id == 115
                ? (simpleProduct?.attributes?.[0]?.label as string)
                : undefined
            }
            hasIcon
          />
        </div>
      </div>
      <div
        className="absolute left-0 right-0 bottom-0 grid grid-cols-1 justify-center p-4 pt-6 lg:p-8 lg:pt-2"
        style={{
          background:
            'linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 1) 20%)',
        }}
      >
        <ButtonMain className="mb-6" variant="Text" onClick={closeFlyout}>
          {t('Continue shopping')}
        </ButtonMain>
        <ButtonMain fullWidth href="/cart" onClick={closeFlyout}>
          {t('Show cart')}
        </ButtonMain>
      </div>
    </div>
  )
}
